// Pure presentation helpers for the ARTIST CNTRL PRO advance catalogue (ART-146). No hooks, no I/O — the
// catalogue, the recall bar, the link modal and the mobile screen all render the same chips, and four local
// copies of "is this link open" is how `holdCountdown` came to hold three opinions about one rule.

import { formatShortDate } from "../../releases/releaseFormat.js";
import type { AdvanceLookView } from "../../../app/hooks/useProduction.js";

/** The three advance kinds. Mirrors the server's `AdvanceKinds`; a look with NO kind is a Standard look. */
export const ADVANCE_KINDS = [
  { value: "tour", label: "Tour" },
  { value: "fest", label: "Fest" },
  { value: "oneoff", label: "One-off" },
] as const;

export type AdvanceKind = (typeof ADVANCE_KINDS)[number]["value"];

/**
 * What the NAME field is called, which follows the kind: a tour has a tour name, a festival has a fest name,
 * a one-off is an event. The spec asks for exactly this and it is the only thing the segmented control
 * changes about the form, so it lives beside the vocabulary rather than inside the modal.
 */
export function nameFieldLabel(kind: string | null | undefined): string {
  return kind === "fest" ? "Fest name" : kind === "oneoff" ? "Event name" : "Tour name";
}

export function namePlaceholder(kind: string | null | undefined): string {
  return kind === "fest" ? "e.g. Bonnaroo" : kind === "oneoff" ? "e.g. Red Rocks" : "e.g. World Tour";
}

/** "World Tour 2026" — the year rides the title when there is one, and is simply absent when there is not. */
export function advanceTitle(look: Pick<AdvanceLookView, "name" | "year">): string {
  return look.year ? `${look.name ?? ""} ${look.year}`.trim() : (look.name ?? "");
}

/**
 * A calendar date for the link chips — "Dec 31, 2026".
 *
 * It delegates to `releaseFormat.formatShortDate` rather than re-deriving the rule, and that is the point of
 * the import: a date-only string parsed as `new Date("2026-12-31")` is MIDNIGHT UTC, which renders as the
 * 30th for every viewer west of Greenwich — so a band in Nashville would be told its link came down a day
 * before it did. That module already parses at local noon. A fourth copy of the rule is what this avoids.
 */
export const formatLinkDate = formatShortDate;

/**
 * The one classification of an advance's PUBLIC link, for the chips every surface draws.
 *
 * FOUR states, not two, and each is a different sentence: the link may not exist yet (nothing to say), it may
 * have run out, it may be behind a password, or it may be open to anyone holding the url. Collapsing "no link"
 * into "open" is the worst of the available mistakes — it would tell a band its advance is public when nothing
 * has been published at all.
 */
export type LinkState = "none" | "expired" | "password" | "open";

export function linkState(look: Pick<AdvanceLookView, "hasVenueToken" | "hasPassword" | "expired">): LinkState {
  if (!look.hasVenueToken) return "none";
  if (look.expired) return "expired";
  return look.hasPassword ? "password" : "open";
}

/** One chip's worth of words for a link, or null when there is no link to describe. */
export function linkChip(
  look: Pick<AdvanceLookView, "hasVenueToken" | "hasPassword" | "expired" | "expiresOn">,
): { label: string; tone: "muted" | "warning" | "neutral" | "success" } | null {
  switch (linkState(look)) {
    case "none":
      return null;
    case "expired":
      // The DATE goes on the recall bar, not here: a catalogue row has one line, and "Expired" is the fact
      // that changes what the reader does.
      return { label: "Expired", tone: "warning" };
    case "password":
      return { label: look.expiresOn ? `🔒 Through ${formatLinkDate(look.expiresOn)}` : "🔒 Password", tone: "neutral" };
    case "open":
      // "Open access", not "Open link" (ART-221): "Open link" read like a control you could click, next to the
      // row's actual buttons. This is a STATUS — the public url is open to anyone holding it.
      return look.expiresOn
        ? { label: `Through ${formatLinkDate(look.expiresOn)}`, tone: "neutral" }
        : { label: "Open access", tone: "success" };
  }
}
